import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../../../components/feature/Navbar';
import { Footer } from '../../../components/feature/Footer';

const UrbanSalonDemo: React.FC = () => {
  useEffect(() => {
    document.title = 'Urban Salon & Spa - Demo | BusinessWebsitePro';
  }, []);

  const services = [
    { name: 'Haircut & Styling', price: 'From $65', duration: '60 min', icon: 'ri-scissors-line' },
    { name: 'Hair Coloring', price: 'From $120', duration: '120 min', icon: 'ri-palette-line' },
    { name: 'Facial Treatment', price: 'From $95', duration: '75 min', icon: 'ri-heart-pulse-line' },
    { name: 'Massage Therapy', price: 'From $110', duration: '90 min', icon: 'ri-hand-heart-line' },
    { name: 'Manicure & Pedicure', price: 'From $75', duration: '60 min', icon: 'ri-hand-sanitizer-line' },
    { name: 'Bridal Package', price: 'From $350', duration: '180 min', icon: 'ri-gift-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-6">
          <Link to="/portfolio" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition-colors">
            <i className="ri-arrow-left-line"></i>
            Back to Portfolio
          </Link>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-pink-100 text-pink-700 text-sm font-semibold px-4 py-2 rounded-full mb-6">
                Beauty & Wellness Demo
              </span>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">Urban Salon & Spa</h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Luxury beauty and wellness services at your fingertips. This elegant website features online booking, service galleries, and a seamless customer experience.
              </p>
              <div className="flex flex-wrap gap-4">
                <button className="bg-pink-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap">
                  Book Appointment
                </button>
                <button className="bg-white text-pink-600 px-8 py-4 rounded-xl font-semibold border-2 border-pink-600 hover:bg-pink-50 transition-all duration-300 whitespace-nowrap">
                  View Services
                </button>
              </div>
            </div>
            <div className="w-full h-96 rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/98fda5c0c0fb80cdd69269cfd4e0579d.jpg" 
                alt="Urban Salon & Spa Website" 
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600">Pamper yourself with our premium treatments</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300">
                <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-3xl text-pink-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.name}</h3>
                <div className="flex items-center gap-4 mb-3">
                  <span className="text-2xl font-bold text-pink-600">{service.price}</span>
                  <span className="text-gray-500">• {service.duration}</span>
                </div>
                <button className="text-pink-600 font-semibold flex items-center gap-2 hover:gap-3 transition-all duration-300 cursor-pointer whitespace-nowrap">
                  Book Now
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Work</h2>
            <p className="text-xl text-gray-600">See the transformations we create</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <div key={item} className="w-full h-80 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
                <img 
                  src={`https://readdy.ai/api/search-image?query=professional%20salon%20hair%20styling%20result%2C%20beautiful%20hairstyle%20transformation%2C%20elegant%20beauty%20treatment%20outcome%2C%20soft%20lighting%2C%20clean%20background&width=400&height=500&seq=salon-gallery-${item}&orientation=portrait`}
                  alt={`Gallery ${item}`}
                  className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Website Features</h2>
            <p className="text-xl text-gray-600">Everything your salon needs to succeed online</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-calendar-line text-3xl text-pink-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Online Booking</h3>
              <p className="text-gray-600">24/7 appointment scheduling with automated confirmations</p>
            </div>

            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-image-line text-3xl text-pink-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Service Gallery</h3>
              <p className="text-gray-600">Showcase your best work with beautiful photo galleries</p>
            </div>

            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-star-line text-3xl text-pink-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Client Reviews</h3>
              <p className="text-gray-600">Display testimonials to build trust and credibility</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to Grow Your Salon Business?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Get a beautiful website that attracts more clients and makes booking appointments effortless.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-[#14B8A6] text-white px-10 py-5 rounded-xl font-semibold hover:bg-[#0d9488] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            Start Your Project
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UrbanSalonDemo;
